// LiveKit video-publish tuning (spec: docs/specs/2026-07-29-video-tuning-round4-and-telemetry-design.md).
// The SINGLE source of the tunables so the portal + kiosk adapters can never drift.
// Retune here; both apps and the one test follow.
//
// Round 4 (2026-07-29): ROLE-AWARE — the two publishers face opposite constraints.
// Evidence (2026-07-29 audit): 1,054 SFU congestion events in 13 days, ALL uplink,
// 94% on the pilot agent's ~2 Mbps hotspot; round 3's fixed 3.5 Mbps 1080p single
// layer left the SFU nothing to step down to, so every dip rendered as a freeze.
// - kiosk (guest -> agent): 720p @ 1.5 Mbps PLUS simulcast rungs [180p, 360p] and
//   dynacast. The SFU can now serve the agent's constrained downlink a lower rung
//   under congestion (layer switch, not freeze). Total kiosk uplink ~2.1 Mbps —
//   still below round 3's single 3.5 Mbps stream.
// - portal (agent -> kiosk): single 720p @ 1.2 Mbps. Fits a ~2 Mbps agent uplink
//   with headroom (audio + optional captions ride the same link). NO simulcast on
//   the agent side: round 2 found Chrome simulcast forced software H.264 (CPU risk
//   on agent PCs), and the hotel's downlink doesn't need a ladder.
// - Codec stays H.264 (NOT VP9): the kiosk is an iPad — iOS/WebKit reliably encodes
//   only H.264, in HARDWARE (VideoToolbox). SDK-verified (livekit-client 2.20.0):
//   nothing client-side blocks H.264 simulcast (only E2EE-on-Safari and SVC codecs
//   have guards); the real-iPad night-1 check remains the acceptance gate.
// - "balanced" degradation: under a constrained link the encoder itself also steps
//   resolution/framerate down instead of freezing at full size.
// - adaptiveStream stays OFF everywhere, deliberately: the portal renders the remote
//   track via a hidden in-tab stage plus a manually-wired DocPiP element, which
//   element-visibility-driven adaptation would misread (it could pause the video the
//   agent is actually watching in the tile). SFU congestion-driven layer selection
//   works without it.
// @lc/shared stays dependency-free (no livekit-client import): simulcast rungs are
// returned as plain data; each adapter constructs `new VideoPreset(...)` from them
// after its dynamic import of livekit-client.

export type VideoRole = "kiosk" | "portal";

interface LayerSpec {
  width: number;
  height: number;
  maxBitrate: number;
  maxFramerate: number;
}

/** Tuning knobs. See the spec's decision table + the round-4 note above. */
export const LIVEKIT_VIDEO_TUNING = {
  videoCodec: "h264",
  degradationPreference: "balanced",
  kiosk: {
    /** The top simulcast rung (also the capture resolution). */
    primary: { width: 1280, height: 720, maxBitrate: 1_500_000, maxFramerate: 30 },
    /** Lower rungs, low -> high (livekit-client's h180/h360 preset values). */
    simulcastLayers: [
      { width: 320, height: 180, maxBitrate: 160_000, maxFramerate: 20 },
      { width: 640, height: 360, maxBitrate: 450_000, maxFramerate: 20 },
    ] as readonly LayerSpec[],
    dynacast: true,
  },
  portal: {
    /** The single published layer. */
    primary: { width: 1280, height: 720, maxBitrate: 1_200_000, maxFramerate: 30 },
    simulcastLayers: null,
    dynacast: false,
  },
} as const;

export interface LiveKitVideoOptions {
  roomOptions: {
    dynacast: boolean;
    videoCaptureDefaults: { resolution: { width: number; height: number } };
    publishDefaults: {
      videoCodec: "h264";
      videoEncoding: { maxBitrate: number; maxFramerate: number };
      simulcast: boolean;
      degradationPreference: "balanced";
    };
  };
  captureOptions: { resolution: { width: number; height: number } };
  /**
   * Lower simulcast rungs (low -> high) as plain data, or null when the role
   * publishes a single layer. The adapter maps each entry to
   * `new VideoPreset(width, height, maxBitrate, maxFramerate)` and sets
   * `publishDefaults.videoSimulcastLayers` — VideoPreset instances are required
   * at runtime (livekit-client reads `.encoding`/`.resolution` getters), which
   * is why this dependency-free module cannot construct them itself.
   */
  simulcastLayers: LayerSpec[] | null;
}

/**
 * Build the LiveKit Room + capture options for one side of the call from the
 * shared tuning constant. Both adapters call this with their role, pass
 * `roomOptions` to `new Room(...)` (mapping `simulcastLayers` onto
 * `publishDefaults.videoSimulcastLayers` first, when present), and
 * `captureOptions` to `createLocalVideoTrack(...)`. Capture resolution is set
 * BOTH on the room defaults and returned as standalone `captureOptions` because
 * `createLocalVideoTrack` does not read the room's `videoCaptureDefaults`.
 */
export function buildLiveKitVideoOptions(role: VideoRole): LiveKitVideoOptions {
  const { videoCodec, degradationPreference } = LIVEKIT_VIDEO_TUNING;
  const tuning = LIVEKIT_VIDEO_TUNING[role];
  const resolution = { width: tuning.primary.width, height: tuning.primary.height };
  return {
    roomOptions: {
      dynacast: tuning.dynacast,
      videoCaptureDefaults: { resolution },
      publishDefaults: {
        videoCodec,
        videoEncoding: {
          maxBitrate: tuning.primary.maxBitrate,
          maxFramerate: tuning.primary.maxFramerate,
        },
        simulcast: tuning.simulcastLayers !== null,
        degradationPreference,
      },
    },
    captureOptions: { resolution },
    simulcastLayers: tuning.simulcastLayers === null ? null : tuning.simulcastLayers.map((l) => ({ ...l })),
  };
}
